import Zone from "@insite/client-framework/Components/Zone";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getProductListDataView } from "@insite/client-framework/Store/Pages/ProductList/ProductListSelectors";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import { ProductListPageContext } from "@insite/content-library/Pages/ProductListPage";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import breakpointMediaQueries from "@insite/mobius/utilities/breakpointMediaQueries";
import React, { FC } from "react";
import { connect } from "react-redux";
import { css } from "styled-components";

const enum fields {
    cssClass = "cssClass",
}

const mapStateToProps = (state: ApplicationState) => ({
    view: state.pages.productList.view,
    productsDataView: getProductListDataView(state),
});

export interface ProductListControlsContainerStyles {
    container?: GridContainerProps;
    countGridItem?: GridItemProps;
    sortGridItem?: GridItemProps;
    paginationItem?: GridItemProps;
    viewSelectGridItem?: GridItemProps;
}

interface OwnProps extends WidgetProps {
    fields: {
        [fields.cssClass]: string;
    };
}

type Props = ReturnType<typeof mapStateToProps> & WidgetProps & OwnProps;

export const controlsContainerStyles: ProductListControlsContainerStyles = {
    container: {
        gap: 0,
        css: css`
            background: #f1f6fb;
            padding: 5px 15px 10px;
            margin-bottom: 1rem;
            &.footerPagination {
                > div {
                    justify-content: flex-end;
                    div[class^="PaginationStyle"] {
                        justify-content: center;
                        @media (max-width: 1199px) and (min-width: 992px) {
                            justify-content: center;
                        }
                    }
                }
                [data-test-selector="selectViewCommon"] {
                    display: none;
                }
            }
        `,
    },
    countGridItem: {
        width: [12, 6, 6, 2, 2],
    },
    sortGridItem: {
        width: [12, 6, 6, 4, 3],
    },
    paginationItem: {
        width: [12, 12, 12, 5, 6],
        css: css`
            ${({ theme }: { theme: BaseTheme }) => breakpointMediaQueries(theme, [null, css``, null, null, null])}
        `,
    },
    viewSelectGridItem: {
        width: [12, 12, 1, 1, 1],
        css: css`
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    css`
                        display: none;
                    `,
                    css`
                        display: none;
                    `,
                    null,
                    null,
                    null,
                ])}
        `,
    },
};

const styles = controlsContainerStyles;

const ProductListControlsContainer: FC<Props> = ({ id, view, fields, productsDataView }) => {
    if (!productsDataView.value) {
        return null;
    }

    const products = productsDataView.value;

    if (products.length === 0) {
        return null;
    }
    return (
        <GridContainer {...styles.container} className={fields.cssClass}>
            <GridItem {...styles.countGridItem}>
                <Zone contentId={id} zoneName="Content00" />
            </GridItem>
            <GridItem {...styles.sortGridItem}>
                <Zone contentId={id} zoneName="Content01" />
            </GridItem>
            <GridItem {...styles.paginationItem}>
                <Zone contentId={id} zoneName="Content02" />
            </GridItem>
            <GridItem {...styles.viewSelectGridItem} data-test-selector="selectViewCommon">
                <Zone contentId={id} zoneName="Content03" />
            </GridItem>
        </GridContainer>
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps)(ProductListControlsContainer),
    definition: {
        group: "Product List",
        displayName: "List Controls Container",
        allowedContexts: ["ProductListPage"],
        fieldDefinitions: [
            {
                name: fields.cssClass,
                displayName: "Add CSS Class",
                editorTemplate: "TextField",
                defaultValue: "",
                fieldType: "General",
            },
        ],
    },
};

export default widgetModule;
